"use client"

import { useEffect, useRef, useState } from "react"
import Link from "next/link"

import { BoothMark } from "@/components/ui/booth-mark"
import { chatSound } from "@/lib/chat/sounds"
import { autolinkBooth, isBoothLink, withChatAttribution } from "@/lib/chat/links"
import ReactMarkdown from "react-markdown"
import remarkGfm from "remark-gfm"
import { useChat } from "@ai-sdk/react"
import { DefaultChatTransport, type UIMessage } from "ai"
import { ArrowUp, Square, X } from "lucide-react"

import { EASE, usePrefersReducedMotion } from "@/components/glass/glass-kit"
import { GRAIN_URL } from "@/components/public-nav-glass"

// ============================================================
// BOOTH — the assistant, as it appears on the marketing pages.
//
// A launcher in the bottom-left corner and a panel above it, in the nav's
// dark liquid glass (GLASS_* below) rather than colours of its own, so it
// reads as the same chrome on every page it is dropped on.
//
// The answers, and the rules they follow, are server side in
// lib/chat/assistant.ts. This file is the window.
// ============================================================

/** What a cold visitor is most likely to want, in their words rather than
 *  ours. Shown once, on the empty panel, and gone after the first message. */
/** The panel's size is shared by the silhouette and the content above it, so
 *  the shape the filter draws is exactly the shape the text sits in. */
const PANEL_W = "min(23rem, 80svw)"
const PANEL_H = "min(32rem, 68vh)"

// The material. The nav's dark liquid glass (public-nav-glass.tsx), not solid
// black: /search is itself black, and a black panel on a black page was a
// column of floating text with no container around it. A near-black fill at
// 92% with the page blurred through it, a hairline edge and a top sheen is
// what lets the box read on black and still read as glass over a listing's
// photos. It was 72%, and over a light listing or the search page's drift
// the panel came out grey; at 92% it stays near-black everywhere, a step
// above true black, with the blur only tinting the edge of what's behind.
// Dark in both colour modes, like the nav: the text inside is white,
// and a light-page blur would wash it out.
const GLASS_FILL = "rgba(10,10,11,0.92)"
const GLASS_EDGE = "rgba(255,255,255,0.12)"
const GLASS_BLUR = "blur(30px) saturate(165%)"
const GLASS_SHEEN =
  "linear-gradient(168deg, rgba(255,255,255,0.14) 0%, rgba(255,255,255,0.04) 22%, transparent 46%)"
const GLASS_SHADOW = "0 30px 70px -28px rgba(0,0,0,0.9)"

/** `prefill` puts the words in the box and waits, so the person types their
    own city. We used to guess it: the device position, or failing that the
    IP's city, which put people a suburb or two away from where they were
    and then said so with confidence. Asking costs a word; guessing wrong
    cost the answer. */
const OPENERS: { label: string; prefill?: string }[] = [
  { label: "I have empty chairs. How does listing work?" },
  { label: "What does BOOTH cost?" },
  { label: "Find an open chair in my city", prefill: "Find an open chair in " },
]

const textOf = (message: UIMessage) =>
  message.parts
    .filter((part): part is { type: "text"; text: string } => part.type === "text")
    .map((part) => part.text)
    .join("")

/** The answer, rendered. Markdown because a list of shops reads better as a
 *  list, with the element styles kept tight so a chat panel never turns into
 *  a document: no headings bigger than the text around them, and links that
 *  open away from the page the person is reading. */
function Answer({ text }: { text: string }) {
  return (
    // A markdown "loose" list wraps every item in its own paragraph, which is
    // where the big gaps between shops came from: [&_li>p]:m-0 flattens them.
    <div className="space-y-1.5 text-[12px] leading-[1.45] [&_li>p]:m-0 [&_li]:ml-3.5 [&_li]:list-disc [&_li]:pl-0 [&_ol]:space-y-0.5 [&_p]:leading-[1.45] [&_strong]:font-semibold [&_ul]:space-y-0.5">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          // A listing URL is a uuid and wraps over two lines if it is ever
          // shown as itself. Anything that arrives as a bare link gets a
          // label instead; a link the model titled keeps its title.
          //
          // And it has to point at us. react-markdown already refuses
          // javascript: and friends, but not a link to somewhere else — and
          // the one thing a person who got a shop listed could try is to
          // have the assistant hand a stranger a link they control. An
          // off-site href renders as its own text, unlinked, so a reader
          // can see it and nobody can be walked anywhere by a sentence that
          // looks like it came from BOOTH.
          a: ({ href, children }) => {
            const label = String(Array.isArray(children) ? children.join("") : (children ?? ""))
            const bare = /^https?:\/\//.test(label)
            if (!isBoothLink(href)) return <span>{label || href}</span>
            return (
              <a
                href={withChatAttribution(href ?? "")}
                target="_blank"
                rel="noopener noreferrer"
                className="whitespace-nowrap underline underline-offset-2"
              >
                {bare ? "View listing" : children}
              </a>
            )
          },
          // A heading inside a 24rem panel is noise; render it as emphasis.
          h1: ({ children }) => <p className="font-semibold">{children}</p>,
          h2: ({ children }) => <p className="font-semibold">{children}</p>,
          h3: ({ children }) => <p className="font-semibold">{children}</p>,
        }}
      >
        {autolinkBooth(text)}
      </ReactMarkdown>
    </div>
  )
}

export function BoothAssistant() {
  const [open, setOpen] = useState(false)
  const [draft, setDraft] = useState("")
  const scroller = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  // One id per mount, so the turns of this conversation group together in
  // the transcript. Not stored anywhere, not tied to an account.
  const session = useRef<string>(typeof crypto !== "undefined" && "randomUUID" in crypto ? crypto.randomUUID() : "")
  const { messages, sendMessage, status, error, stop } = useChat({
    transport: new DefaultChatTransport({
      api: "/api/chat",
      prepareSendMessagesRequest: ({ messages: outgoing }) => ({
        body: {
          messages: outgoing,
          ...(session.current ? { session: session.current } : {}),
          path: typeof window !== "undefined" ? window.location.pathname : undefined,
        },
      }),
    }),
  })

  const busy = status === "submitted" || status === "streaming"
  // Busy AND nothing of the answer on screen yet — which covers the tool
  // call, where the model is quiet for a second or two while a city is
  // searched.
  const last = messages[messages.length - 1]
  const awaitingAnswer = busy && (last?.role !== "assistant" || textOf(last).length === 0)

  // Follow the answer as it streams, and land the caret in the box when the
  // panel opens — the keyboard is how most people will use this.
  useEffect(() => {
    scroller.current?.scrollTo({ top: scroller.current.scrollHeight, behavior: "smooth" })
  }, [messages, status])

  // Focus the box on open — but only where focusing does not summon a
  // keyboard. On a phone the panel appeared with half of it already covered,
  // before the person had decided to type anything. A coarse pointer is the
  // test: touch waits for a tap on the field, a mouse keeps the caret.
  useEffect(() => {
    if (!open) return
    const touch = typeof window !== "undefined" && window.matchMedia?.("(pointer: coarse)").matches
    if (!touch) inputRef.current?.focus()
  }, [open])

  // The answer landing. Watching the transition out of a working state is
  // what makes this one cue per reply rather than one per token — and the
  // ref means a re-render for any other reason cannot retrigger it.
  const wasBusy = useRef(false)
  useEffect(() => {
    if (wasBusy.current && status === "ready") chatSound.receive()
    wasBusy.current = status === "submitted" || status === "streaming"
  }, [status])

  const send = (text: string) => {
    const trimmed = text.trim()
    if (!trimmed || busy) return
    setDraft("")
    chatSound.send()
    void sendMessage({ text: trimmed })
  }

  const toggle = () => {
    // Worked out BEFORE setOpen, never inside it. A state updater has to be
    // pure: React is free to call it more than once, and when the cue lived
    // in there it fired about ten times per click.
    const opening = !open
    if (opening) chatSound.open()
    else chatSound.close()
    setOpen(opening)
  }

  const reduced = usePrefersReducedMotion()
  // Slow on purpose. The neck between the dot and the panel is the whole
  // effect, and at 420ms it was over before it formed — the shape has to
  // have time to stretch, thin and let go. The easing overshoots nothing;
  // it starts fast and settles, the way a drop of liquid does.
  const move = reduced ? "0ms" : "900ms"
  const LIQUID = "cubic-bezier(0.16, 1, 0.3, 1)"

  return (
    // pointer-events-none on the wrapper, auto only on the things you can
    // actually press. Without it this box — the panel's full width, even
    // closed and invisible — sat over the bottom of every page and ate the
    // taps meant for whatever was under it: on a phone, the docked
    // Barber/Owner switch.
    <div className="pointer-events-none fixed bottom-5 left-5 z-50 print:hidden">
      {/* The goo filter. Blur the shapes, then crush the alpha ramp back to a
          hard edge: where two blurred edges overlap they survive the crush as
          one shape, which is the neck. It only ever touches the silhouette
          layer below — run it over text and the text blurs with it. */}
      {/* 1px rather than h-0 w-0: Safari will not resolve a filter defined
          inside a zero-sized SVG, which is why the neck between the dot and
          the panel vanished there while Chrome drew it. Hidden by size and
          opacity, not display:none — that breaks it in Safari too. */}
      <svg
        aria-hidden
        width="1"
        height="1"
        className="pointer-events-none absolute"
        style={{ width: 1, height: 1, overflow: "hidden", opacity: 0 }}
      >
        <defs>
          <filter id="booth-goo">
            <feGaussianBlur in="SourceGraphic" stdDeviation="11" result="blur" />
            <feColorMatrix
              in="blur"
              mode="matrix"
              values="1 0 0 0 0  0 1 0 0 0  0 0 1 0 0  0 0 0 26 -12"
              result="goo"
            />
            <feBlend in="SourceGraphic" in2="goo" />
          </filter>
        </defs>
      </svg>

      <div className="relative flex flex-col items-start">
        {/* The shapes. Solid, wordless, and the only thing the filter sees.
            The panel grows out of the launcher, so for the first part of the
            transition the two are close enough to read as one body with a
            neck between them — the way the reference connects to its dot. */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 flex flex-col items-start justify-end"
          style={{
            filter: "url(#booth-goo)",
            // Solid while it morphs — the goo filter crushes alpha, so the
            // shapes themselves cannot be translucent — then down to a smoke
            // once the glass above has faded in, so the page shows through
            // the panel instead of a black slab sitting under it. Open, it
            // only matters where the glass is not: the neck and the dot.
            opacity: open ? 0.35 : 1,
            transition: reduced ? "none" : `opacity 320ms ease ${open ? "440ms" : "0ms"}`,
          }}
        >
          <div
            className="origin-bottom-left rounded-[2rem] bg-black"
            style={{
              width: PANEL_W,
              height: open ? PANEL_H : 0,
              opacity: open ? 1 : 0,
              transform: open ? "scale(1)" : "scale(0.2)",
              transition: `height ${move} ${LIQUID}, transform ${move} ${LIQUID}, opacity ${move} ${LIQUID}`,
            }}
          />
          {/* The gap the neck forms across. Wide enough to separate the two
              shapes, narrower than the blur, which is what lets them join. */}
          <div style={{ height: 10 }} />
          <div className="h-14 w-14 rounded-full bg-black" />
        </div>

        {/* Everything readable sits above the filter, unblurred. */}
        <div
          className="relative overflow-hidden rounded-[2rem]"
          style={{
            width: PANEL_W,
            height: open ? PANEL_H : 0,
            opacity: open ? 1 : 0,
            pointerEvents: open ? "auto" : "none",
            background: GLASS_FILL,
            border: `1px solid ${GLASS_EDGE}`,
            // Constant, never transitioned: animating backdrop-filter re-blurs
            // the whole page every frame (the nav shell learned this).
            backdropFilter: GLASS_BLUR,
            WebkitBackdropFilter: GLASS_BLUR,
            // An inset hairline of light along the top edge, the way the booth
            // cards catch the room, plus the lift off the page.
            boxShadow: `inset 0 1px 0 rgba(255,255,255,0.10), ${GLASS_SHADOW}`,
            transition: reduced
              ? "none"
              : `height ${move} ${LIQUID}, opacity 260ms ease ${open ? "420ms" : "0ms"}`,
          }}
          role="dialog"
          aria-label="Ask BOOTH"
          aria-hidden={!open}
        >
          {/* Specular gloss + grain — the liquid-glass read, the same two
              layers the nav shell carries. */}
          <div aria-hidden className="pointer-events-none absolute inset-0" style={{ background: GLASS_SHEEN }} />
          <div
            aria-hidden
            className="pointer-events-none absolute inset-0 mix-blend-overlay"
            style={{ backgroundImage: GRAIN_URL, opacity: 0.22 }}
          />
          <div className="relative flex h-full flex-col" style={{ color: "#F7F7F5" }}>
            <header className="flex items-center justify-between px-4 pb-2 pt-3.5">
              <div className="flex items-center gap-2.5">
                {/* The real mark, not a stand-in: the same two rings the nav
                    and the wordmark use, in currentColor. */}
                <BoothMark size={22} className="shrink-0 text-white/80" />
                {/* Name only. The paragraph under it already says what this
                    is for, and a subtitle repeating it in six words was the
                    kind of line a product writes to fill a slot. */}
                <p className="text-[13px] font-semibold">Ask BOOTH</p>
              </div>
              <button
                type="button"
                onClick={() => {
                  chatSound.close()
                  setOpen(false)
                }}
                aria-label="Close"
                className="rounded-full p-1.5 transition-opacity hover:opacity-70"
              >
                <X className="h-4 w-4" />
              </button>
            </header>

            {/* overscroll-contain: without it, reaching the top or bottom of
                this list hands the scroll to the page behind, so a thumb
                inside the panel moves the landing page under it. */}
            <div
              ref={scroller}
              className="flex-1 space-y-2.5 overflow-y-auto overscroll-contain px-4 py-3"
            >
              {messages.length === 0 && (
                <div className="space-y-3">
                  <p className="text-[12px] leading-[1.45] text-white/50">
                    Ask anything about renting a chair, listing one, or what BOOTH charges.
                  </p>
                  <div className="flex flex-col items-start gap-1.5">
                    {OPENERS.map((opener) => (
                      <button
                        key={opener.label}
                        type="button"
                        onClick={() => {
                          if (!opener.prefill) return send(opener.label)
                          setDraft(opener.prefill)
                          inputRef.current?.focus()
                        }}
                        className="rounded-full border border-white/15 px-2.5 py-1 text-left text-[11.5px] transition-colors hover:border-white/35"
                      >
                        {opener.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {messages.map((message) => {
                const mine = message.role === "user"
                const text = textOf(message)
                if (!text && !mine) return null
                return (
                  <div key={message.id} className={mine ? "flex justify-end" : "flex justify-start"}>
                    <div
                      className={
                        mine
                          ? "max-w-[86%] whitespace-pre-wrap rounded-2xl bg-white px-3 py-2 text-[12px] leading-[1.45] text-[#08080A]"
                          : "max-w-[92%] text-[12px] leading-[1.45]"
                      }
                    >
                      {mine ? text : <Answer text={text} />}
                    </div>
                  </div>
                )
              })}

              {/* Shown for the WHOLE wait, not just the first moment of it.
                  The old version watched "submitted" only, so the seconds
                  spent running a city search — which is the longest part —
                  showed an empty panel. This watches for an answer that has
                  not started arriving yet, whatever the assistant is doing. */}
              {awaitingAnswer && (
                <div className="flex justify-start" aria-live="polite" aria-label="Thinking">
                  <div className="booth-typing flex items-center gap-1 rounded-2xl bg-white/[0.07] px-3 py-2.5">
                    <span className="block h-[5px] w-[5px] rounded-full bg-white" />
                    <span className="block h-[5px] w-[5px] rounded-full bg-white" />
                    <span className="block h-[5px] w-[5px] rounded-full bg-white" />
                  </div>
                </div>
              )}

              {error && (
                <p className="text-[11.5px] text-red-400">
                  That didn&apos;t go through. Try again, or use the{" "}
                  <Link href="/help#contact" className="underline">
                    contact form
                  </Link>
                  .
                </p>
              )}
            </div>

            {/* The composer is its own pill inside the panel, not a bar across
                the bottom of it. The panel is already black, so the field
                reads by sitting a step off it rather than by a border. */}
            <form
              onSubmit={(e) => {
                e.preventDefault()
                send(draft)
              }}
              className="px-3.5 pb-4 pt-1"
            >
              <div className="flex items-center gap-1.5 rounded-full bg-white/[0.07] py-1.5 pl-4 pr-1.5 transition-colors focus-within:bg-white/[0.11]">
                <input
                  ref={inputRef}
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  placeholder="Ask about chairs, rent, or listing"
                  maxLength={1000}
                  className="min-w-0 flex-1 bg-transparent text-[12px] outline-none placeholder:text-white/35"
                />
                {busy ? (
                  <button
                    type="button"
                    onClick={() => stop()}
                    aria-label="Stop"
                    className="shrink-0 rounded-full bg-white/15 p-1.5 transition-opacity hover:opacity-70"
                  >
                    <Square className="h-3.5 w-3.5" />
                  </button>
                ) : (
                  <button
                    type="submit"
                    disabled={!draft.trim()}
                    aria-label="Send"
                    className="shrink-0 rounded-full bg-white p-1.5 text-[#08080A] transition-opacity disabled:opacity-25"
                  >
                    <ArrowUp className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>

        <div style={{ height: 10 }} />

        {/* The dot. The blob behind it is the shape; this is the hit area and
            the mark, which must not be touched by the filter. */}
        <button
          type="button"
          onClick={toggle}
          aria-label={open ? "Close the assistant" : "Ask BOOTH"}
          aria-expanded={open}
          className="group pointer-events-auto relative isolate flex h-14 w-14 items-center justify-center overflow-hidden rounded-full transition-transform duration-200 hover:scale-[1.06] active:scale-95"
          style={{
            // The panel's glass as a circle, open or closed. Open, it used to
            // go transparent and let the solid blob behind it through, so the
            // dot and the panel read as one black piece — but the blob is a
            // smoke now, and a transparent dot on the black /search page was
            // a close cross floating under the panel with nothing around it.
            // A glass dot under a glass panel, joined by the smoky neck, is
            // the honest version of "one piece".
            background: GLASS_FILL,
            border: `1px solid ${GLASS_EDGE}`,
            backdropFilter: GLASS_BLUR,
            WebkitBackdropFilter: GLASS_BLUR,
            boxShadow: "inset 0 1px 0 rgba(255,255,255,0.10), 0 18px 40px -20px rgba(0,0,0,0.9)",
            ["--cta-sheen" as string]: "rgba(255,255,255,0.30)",
          }}
        >
          <span
            aria-hidden
            className="pointer-events-none absolute inset-0"
            style={{ clipPath: "inset(0 round 9999px)" }}
          >
            <span
              aria-hidden
              className="cta-gloss pointer-events-none absolute inset-0"
              style={{
                background:
                  "linear-gradient(180deg, rgba(255,255,255,0.10) 0%, rgba(255,255,255,0.02) 44%, rgba(0,0,0,0.22) 100%)",
                mixBlendMode: "overlay",
              }}
            />
            <span aria-hidden className="cta-sheen pointer-events-none absolute inset-y-0 w-1/3" />
          </span>
          <LauncherGlyph open={open} />
        </button>
      </div>
    </div>
  )
}

/** The glyph in the dot. It turns into a close cross when the panel is
 *  open, rotating rather than swapping, so the dot never blinks. */
function LauncherGlyph({ open }: { open: boolean }) {
  return (
    <span className="relative block h-5 w-5">
      <svg
        viewBox="0 0 20 20"
        className="absolute inset-0 h-5 w-5 transition-all duration-300"
        style={{ opacity: open ? 0 : 1, transform: open ? "rotate(-90deg) scale(0.6)" : "none" }}
        fill="#F7F7F5"
        aria-hidden
      >
        {/* Still, and at their first size. A goo filter and a drift were both
            tried here and both read as a smudge at this scale; the panel
            keeps the liquid, the mark keeps its shape. */}
        <circle cx="7" cy="6.5" r="3.6" />
        <circle cx="13.6" cy="9.4" r="2.9" />
        <circle cx="8.2" cy="14" r="3.1" />
      </svg>
      <svg
        viewBox="0 0 20 20"
        className="absolute inset-0 h-5 w-5 transition-all duration-300"
        style={{ opacity: open ? 1 : 0, transform: open ? "none" : "rotate(90deg) scale(0.6)" }}
        stroke="#F7F7F5"
        strokeWidth="1.8"
        strokeLinecap="round"
        fill="none"
        aria-hidden
      >
        <path d="M6 6l8 8M14 6l-8 8" />
      </svg>
    </span>
  )
}
